import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import { SalesCenterWebhook, SalesCenterWebhookPageState } from './typings'
import { StoreData } from '../reducers/typings'
import { loadFilterPreferences } from '@user/store/userPreferences'
import {
  createSCWebHook,
  deleteSCWebHook,
  getAllSCWebHooks,
  getSelectedSCWebHook,
  updateSCWebHook,
} from '@user/store/scWebHooks/api'

const scWebHookAdapter = createEntityAdapter<SalesCenterWebhook>({
  sortComparer: (a, b) => a.email?.localeCompare(b.email),
})

export const initialState =
  scWebHookAdapter.getInitialState<SalesCenterWebhookPageState>({
    page: 1,
    perPage: 10,
    orderBy: 'email desc',
    selectedWebHook: undefined,
  })

const salesCenterWebhooks = createSlice({
  name: 'salesCenterWebhooks',
  initialState,
  reducers: {
    setOrderBy: (state, { payload }: PayloadAction<string>) => {
      state.orderBy = payload
    },
    setPage: (state, { payload }: PayloadAction<number>) => {
      state.page = payload
    },
    setPerPage: (state, { payload }: PayloadAction<number>) => {
      state.perPage = payload
    },
    setSelectedWebHook: (
      state,
      { payload }: PayloadAction<SalesCenterWebhook | undefined>,
    ) => {
      state.selectedWebHook = payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllSCWebHooks.fulfilled, (state, { payload }) => {
      scWebHookAdapter.setAll(state, payload)
    })
    builder.addCase(createSCWebHook.fulfilled, (state, { payload }) => {
      scWebHookAdapter.addOne(state, payload)
    })
    builder.addCase(getSelectedSCWebHook.fulfilled, (state, { payload }) => {
      scWebHookAdapter.upsertOne(state, payload)
    })
    builder.addCase(updateSCWebHook.fulfilled, (state, { payload }) => {
      scWebHookAdapter.upsertOne(state, payload)
    })
    builder.addCase(deleteSCWebHook.fulfilled, (state, { payload }) => {
      scWebHookAdapter.removeOne(state, payload)
    })
    builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
      const userPref = payload.find((p) => p.type === 'scWebhooks')
      state.orderBy = userPref
        ? JSON.parse(userPref.filter).orderBy
        : 'email desc'
      return state
    })
  },
})

export const { setOrderBy, setPage, setPerPage, setSelectedWebHook } =
  salesCenterWebhooks.actions

export default salesCenterWebhooks.reducer

export const scWebHookAdapterSelectors =
  scWebHookAdapter.getSelectors<StoreData>((state) => state.salesCenterWebhooks)

export const { selectAll: selectAllSCWebHooks } = scWebHookAdapterSelectors

export const selectSCWebHookFilters = (state: StoreData) => {
  return state.salesCenterWebhooks
}
